import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBrand } from '@/shared/brand/useBrand';
import { useSession } from '@/shared/auth';
import { supabase } from '@/lib/supabase';
import {
  applyTheme,
  readStoredTheme,
  resolveTheme,
  storeTheme,
  watchSystemTheme,
  type ResolvedTheme,
  type ThemeMode,
  isThemeMode,
  THEME_STORAGE_KEY,
} from './applyTheme';

export interface ThemeContextValue {
  /** What the user chose: light | dark | system. */
  mode: ThemeMode;
  /** What is painted right now: light | dark. */
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const brand = useBrand();
  const session = useSession();
  const hasLocalPreference = useRef(false);
  const [mode, setModeState] = useState<ThemeMode>(() => {
    try {
      hasLocalPreference.current = isThemeMode(localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
      hasLocalPreference.current = false;
    }
    return readStoredTheme();
  });
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(mode));
  const profileWriteVersion = useRef(0);
  const userId = session.status === 'signedIn' ? session.session.user.id : null;

  useEffect(() => {
    if (!userId || hasLocalPreference.current) return;
    let active = true;
    const requestVersion = profileWriteVersion.current;
    void supabase
      .from('profiles')
      .select('theme')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!active || requestVersion !== profileWriteVersion.current || hasLocalPreference.current || !isThemeMode(data?.theme)) return;
        setModeState(data.theme);
        storeTheme(data.theme);
      });
    return () => {
      active = false;
    };
  }, [userId]);

  // Re-apply on mode or brand change. The inline script in index.html already
  // painted the correct theme on first load, so this never causes a flash —
  // it only keeps the DOM in sync afterwards.
  useEffect(() => {
    setResolved(applyTheme(mode, brand.id));
  }, [mode, brand.id]);

  // `system` must track the OS live, not only at boot.
  useEffect(() => {
    if (mode !== 'system') return;
    return watchSystemTheme(() => setResolved(applyTheme('system', brand.id)));
  }, [mode, brand.id]);

  const setMode = useCallback((next: ThemeMode) => {
    profileWriteVersion.current += 1;
    hasLocalPreference.current = true;
    storeTheme(next);
    setModeState(next);
    if (userId) void supabase.from('profiles').update({ theme: next }).eq('id', userId);
  }, [userId]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolved, setMode }),
    [mode, resolved, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
